// Phase 8a — Property Management dashboard
// Sits at `/manage`. Shows portfolio-wide rollup, alerts strip, leases-expiring
// list, open-maintenance list, and a properties table linking into the hub.

import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useFmt } from "@/lib/useFmt";
import {
  AlertTriangle,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  ClipboardList,
  Clock,
  Home,
  Loader2,
  TriangleAlert,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function Kpi({ label, value, icon: Icon, tone = "indigo", testid }) {
  const tones = {
    indigo: "border-indigo-200 bg-indigo-50/40 text-indigo-700",
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-700",
    slate: "border-slate-200 bg-white text-slate-700",
    red: "border-red-200 bg-red-50/40 text-red-700",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border ${tones[tone]} p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md`}
    >
      <div className="flex items-center justify-between">
        <Icon className="h-4 w-4 opacity-80" />
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] opacity-70">
          {label}
        </span>
      </div>
      <p className="mt-3 text-3xl font-bold tracking-tight tabular-nums text-slate-900">
        {value}
      </p>
    </div>
  );
}

function severityTone(s) {
  if (s === "high") return "border-red-200 bg-red-50 text-red-900";
  if (s === "medium") return "border-amber-200 bg-amber-50 text-amber-900";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function StatusPill({ status }) {
  const map = {
    occupied: "bg-emerald-100 text-emerald-700",
    vacant: "bg-slate-100 text-slate-600",
    ending_soon: "bg-amber-100 text-amber-700",
  };
  const label = status === "ending_soon" ? "Ending soon" : status === "occupied" ? "Occupied" : "Vacant";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${map[status] || map.vacant}`}>
      {label}
    </span>
  );
}

export default function Manage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const fmt = useFmt();

  useEffect(() => {
    let alive = true;
    api
      .get("/manage/dashboard")
      .then(({ data }) => alive && setData(data))
      .catch((e) => alive && setError(e?.response?.data?.detail || "Could not load"));
    return () => {
      alive = false;
    };
  }, []);

  if (error) {
    return (
      <AppShell>
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">{error}</div>
      </AppShell>
    );
  }

  if (!data) {
    return (
      <AppShell>
        <div className="flex h-64 items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      </AppShell>
    );
  }

  const { totals, alerts, expiring_leases, open_maintenance_by_priority, properties } = data;
  const hasProps = properties.length > 0;

  return (
    <AppShell>
      <div className="space-y-8 fade-in" data-testid="manage-dashboard">
        {/* Header */}
        <header>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">
            Operations
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Property Management
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Tenants, leases, maintenance, and documents for every property you own.
            Cross-references your underwriting projections to surface actual-vs-modeled variance.
          </p>
        </header>

        {!hasProps && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 p-10 text-center" data-testid="manage-empty">
            <Building2 className="mx-auto h-10 w-10 text-slate-400" />
            <h2 className="mt-4 text-lg font-semibold text-slate-900">No properties yet</h2>
            <p className="mt-1 text-sm text-slate-500">
              Analyze a deal first — once you own a property, manage tenants and maintenance here.
            </p>
            <Button
              className="mt-5 bg-indigo-600 text-white hover:bg-indigo-700"
              onClick={() => navigate("/deals/new")}
              data-testid="manage-create-first-deal"
            >
              Analyze a deal
            </Button>
          </div>
        )}

        {hasProps && (
          <>
            {/* KPIs */}
            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="manage-kpis">
              <Kpi label="Total properties" value={totals.properties_owned} icon={Building2} tone="indigo" testid="kpi-properties" />
              <Kpi label="Occupied" value={totals.properties_occupied} icon={Home} tone="emerald" testid="kpi-occupied" />
              <Kpi label="Vacant" value={totals.properties_vacant} icon={Home} tone="slate" testid="kpi-vacant" />
              <Kpi
                label="Open maintenance"
                value={totals.open_maintenance}
                icon={Wrench}
                tone={totals.emergency_maintenance > 0 ? "red" : "slate"}
                testid="kpi-open-maintenance"
              />
            </section>

            {/* Rent row */}
            <section className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="kpi-rent-actual">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Monthly rent — actual</p>
                <p className="mt-2 text-3xl font-bold tabular-nums text-slate-900">
                  {fmt.money(totals.monthly_rent_actual_total, { cents: false })}
                </p>
                <p className="mt-1 text-xs text-slate-500">{totals.active_leases} active lease{totals.active_leases !== 1 ? "s" : ""}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="kpi-rent-projected">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Monthly rent — underwriting</p>
                <p className="mt-2 text-3xl font-bold tabular-nums text-slate-700">
                  {fmt.money(totals.monthly_rent_projected_total, { cents: false })}
                </p>
                <p className="mt-1 text-xs text-slate-500">Sum of deal projections</p>
              </div>
            </section>

            {/* Alerts */}
            {alerts.length > 0 && (
              <section data-testid="manage-alerts">
                <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">Alerts</h2>
                <ul className="mt-3 space-y-2">
                  {alerts.slice(0, 8).map((a, i) => (
                    <li
                      key={`${a.type}-${a.property_id}-${i}`}
                      data-testid={`manage-alert-${a.type}`}
                      onClick={() => navigate(a.action_url)}
                      className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition hover:shadow-md ${severityTone(a.severity)}`}
                    >
                      {a.severity === "high" ? (
                        <TriangleAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      ) : (
                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-semibold">{a.title}</p>
                        {a.detail && <p className="mt-0.5 text-xs opacity-80">{a.detail}</p>}
                      </div>
                      <ArrowUpRight className="h-4 w-4 opacity-60" />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Two-column: Leases expiring + Open maint */}
            <section className="grid gap-5 lg:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="manage-expiring-leases">
                <div className="mb-4 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-700">
                    Leases ending in next 60 days
                  </h3>
                </div>
                {expiring_leases.length === 0 ? (
                  <p className="text-sm text-slate-400">Nothing on the radar.</p>
                ) : (
                  <ul className="space-y-2.5">
                    {expiring_leases.map((l) => {
                      const prop = properties.find((p) => p.id === l.property_id);
                      const days = Math.ceil((new Date(l.end_date) - new Date()) / (1000 * 60 * 60 * 24));
                      return (
                        <li
                          key={l.id}
                          onClick={() => navigate(`/manage/properties/${l.property_id}`)}
                          className="group flex cursor-pointer items-center justify-between rounded-lg border border-slate-100 px-3 py-2 hover:bg-slate-50"
                          data-testid={`expiring-lease-${l.id}`}
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900">{prop?.address || "Property"}</p>
                            <p className="text-xs text-slate-500">{l.end_date} · {l.monthly_rent} {l.currency}/mo</p>
                          </div>
                          <span className="text-xs font-semibold text-amber-700">{days}d</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="manage-maint-priority">
                <div className="mb-4 flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-indigo-600" />
                  <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-700">
                    Open maintenance by priority
                  </h3>
                </div>
                <ul className="space-y-2">
                  {[
                    ["emergency", "red", "Emergency"],
                    ["high", "orange", "High"],
                    ["medium", "amber", "Medium"],
                    ["low", "slate", "Low"],
                  ].map(([key, tone, label]) => (
                    <li key={key} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5">
                      <span className={`text-sm font-medium text-${tone}-700`}>{label}</span>
                      <span className="text-base font-bold tabular-nums text-slate-900">
                        {open_maintenance_by_priority[key] || 0}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Properties table */}
            <section data-testid="manage-properties-section">
              <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">Properties</h2>
              <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Address</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Rent (actual)</TableHead>
                      <TableHead className="text-right">Rent (model)</TableHead>
                      <TableHead className="text-right">Open maint.</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {properties.map((p) => {
                      const variance = p.monthly_rent_projected
                        ? ((p.monthly_rent_actual - p.monthly_rent_projected) / p.monthly_rent_projected) * 100
                        : null;
                      return (
                        <TableRow
                          key={p.id}
                          data-testid={`mrow-${p.id}`}
                          onClick={() => navigate(`/manage/properties/${p.id}`)}
                          className="cursor-pointer hover:bg-slate-50"
                        >
                          <TableCell className="font-medium text-slate-900">
                            <p className="truncate">{p.address}</p>
                            <p className="text-xs text-slate-500">{p.city}, {p.country}</p>
                          </TableCell>
                          <TableCell><StatusPill status={p.status} /></TableCell>
                          <TableCell className="text-right tabular-nums">
                            {p.monthly_rent_actual ? fmt.money(p.monthly_rent_actual, { cents: false }) : "—"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-slate-500">
                            {p.monthly_rent_projected ? fmt.money(p.monthly_rent_projected, { cents: false }) : "—"}
                            {variance !== null && Math.abs(variance) >= 5 && (
                              <span className={`ml-1 text-[10px] font-semibold ${variance >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                                {variance > 0 ? "+" : ""}{variance.toFixed(0)}%
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {p.open_maintenance > 0 ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                                {p.open_maintenance}
                              </span>
                            ) : (
                              <CheckCircle2 className="ml-auto h-4 w-4 text-emerald-500" />
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-indigo-600 hover:bg-indigo-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/manage/properties/${p.id}`);
                              }}
                              data-testid={`manage-row-${p.id}-go`}
                            >
                              Manage <ArrowUpRight className="ml-1 h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </section>
          </>
        )}
      </div>
    </AppShell>
  );
}
